import { useEffect, useCallback, useRef } from 'react'

interface ShortcutHandlers {
  onUndo?: () => void
  onRedo?: () => void
  onSave?: () => void
  onDownloadPdf?: () => void
  onShowShortcuts?: () => void
}

export function useKeyboardShortcuts(handlers: ShortcutHandlers) {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
    const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey

    // Don't trigger shortcuts when typing in inputs
    const target = e.target as HTMLElement
    const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable

    if (!ctrlOrCmd) return

    switch (e.key.toLowerCase()) {
      case 'z':
        if (isInput) return // Let browser handle undo in inputs
        e.preventDefault()
        if (e.shiftKey) {
          handlersRef.current.onRedo?.()
        } else {
          handlersRef.current.onUndo?.()
        }
        break

      case 'y':
        if (isInput) return
        e.preventDefault()
        handlersRef.current.onRedo?.()
        break

      case 's':
        e.preventDefault()
        handlersRef.current.onSave?.()
        break

      case 'p':
        e.preventDefault()
        handlersRef.current.onDownloadPdf?.()
        break

      case '/':
        e.preventDefault()
        handlersRef.current.onShowShortcuts?.()
        break
    }
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}

/** Returns true if user is on macOS */
export function useIsMac(): boolean {
  return typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0
}
